// frontend/src/features/livestock/CompareImage.tsx
// One shot in a compare frame (design 7.2): the thumb blurred at once, then the
// bigger variant over it when it has loaded, both fitted (object-fit: contain).
// If the variant fails (an expired signed URL, say), "Couldn't load" shows over
// the frame and onError asks for fresh URLs; a new URL tries again. There is
// always an element carrying the alt text (the shot's date).
//
// A lined-up shot (framing, docs/plans/2026-10-29-tank-shot-framing-design.md
// 7.1) is drawn in a box of its framed shape, fitted in the same space, with
// both images placed by framedImageStyle; the shape it reports is the framed one.

import { useState, type CSSProperties, type ReactNode, type SyntheticEvent } from 'react';
import { Box, Center, Stack, Text } from '@mantine/core';
import { IconPhotoOff } from '@tabler/icons-react';
import type { TankPhotoFraming } from '@/types/tankPhotos';
import { framedImageStyle, framedRatio } from './framing';
import classes from './Compare.module.css';

/** A shot as the compare frames show it. */
export interface CompareShotImage {
  /** The variant to show: display (800 px) on the page, full (1920 px) in full screen. */
  src: string | null;
  /** Blurred until `src` has loaded: the 300 px thumb. */
  placeholder: string | null;
  /** ISO 8601 */
  date: string;
  /** How the shot is lined up; absent or null shows it as taken. */
  framing?: TankPhotoFraming | null;
}

export interface CompareImageProps {
  image: CompareShotImage;
  alt: string;
  /** Width / height of the photo (as framed, when lined up), once either image has loaded. */
  onRatio?: (ratio: number) => void;
  /** An image failed to load (its signed URL may have expired). */
  onError?: () => void;
  /** The thumb or the bigger variant has loaded: something is on screen. */
  onShown?: () => void;
}

export function ratioOf(event: SyntheticEvent<HTMLImageElement>): number | null {
  const { naturalWidth, naturalHeight } = event.currentTarget;
  return naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : null;
}

export interface FramedBoxProps {
  framing: TankPhotoFraming;
  /** The photo's width / height, once known: the box takes the framed shape. */
  photoRatio: number | null;
  /** contain: the whole framed shot within the space; cover: fills it, edges cut. */
  fit?: 'contain' | 'cover';
  children: ReactNode;
}

/**
 * A box of the framed shape, centred in its positioned parent, that clips the
 * photo to the crop. Its children are images styled with framedImageStyle.
 */
export function FramedBox({
  framing,
  photoRatio,
  fit = 'contain',
  children,
}: FramedBoxProps): JSX.Element {
  const style = photoRatio
    ? ({ '--framed-ratio': String(framedRatio(photoRatio, framing)) } as CSSProperties)
    : undefined;
  return (
    <Box className={classes.framedArea} data-fit={fit}>
      <Box className={classes.framedBox} style={style} data-testid="framed-box">
        {children}
      </Box>
    </Box>
  );
}

export function CompareImage({
  image,
  alt,
  onRatio,
  onError,
  onShown,
}: CompareImageProps): JSX.Element {
  // Keyed on the URL, so a new shot (or a fresh signed URL) starts over.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [failedPlaceholder, setFailedPlaceholder] = useState<string | null>(null);
  const loaded = !!image.src && loadedSrc === image.src;
  const srcFailed = !!image.src && failedSrc === image.src;
  const placeholderOk = !!image.placeholder && failedPlaceholder !== image.placeholder;
  // Failed: the variant didn't load, or there is no variant and the thumb didn't either.
  const failed = srcFailed || (!image.src && !!image.placeholder && !placeholderOk);
  const showPlaceholder = placeholderOk && !loaded;
  const showImage = !!image.src && !srcFailed;
  const framing = image.framing ?? null;
  const [photoRatio, setPhotoRatio] = useState<number | null>(null);
  const imageStyle = framing ? framedImageStyle(framing) : undefined;

  const reportRatio = (event: SyntheticEvent<HTMLImageElement>): void => {
    onShown?.();
    const ratio = ratioOf(event);
    if (!ratio) return;
    setPhotoRatio(ratio);
    onRatio?.(framedRatio(ratio, framing));
  };

  const images = (
    <>
      {showPlaceholder && (
        <img
          className={classes.placeholder}
          src={image.placeholder ?? undefined}
          alt={showImage || failed ? '' : alt}
          aria-hidden={showImage || failed ? true : undefined}
          decoding="async"
          draggable={false}
          style={imageStyle}
          onLoad={reportRatio}
          onError={() => {
            setFailedPlaceholder(image.placeholder);
            onError?.();
          }}
        />
      )}
      {showImage && (
        <img
          key={image.src}
          className={classes.image}
          data-loaded={loaded || undefined}
          src={image.src ?? undefined}
          alt={alt}
          decoding="async"
          draggable={false}
          style={imageStyle}
          onLoad={(event) => {
            setLoadedSrc(image.src);
            reportRatio(event);
          }}
          onError={() => {
            setFailedSrc(image.src);
            onError?.();
          }}
        />
      )}
    </>
  );

  return (
    <>
      {framing ? (
        <FramedBox framing={framing} photoRatio={photoRatio}>
          {images}
        </FramedBox>
      ) : (
        images
      )}
      {failed ? (
        <Center
          className={classes.failed}
          role="img"
          aria-label={`${alt}, couldn't load`}
          data-testid="compare-image-failed"
        >
          <Stack className={classes.failedBadge} align="center" gap={4} aria-hidden>
            <IconPhotoOff size={24} />
            <Text size="sm">Couldn&apos;t load</Text>
          </Stack>
        </Center>
      ) : (
        // Nothing to draw yet: the alt text still names the shot.
        !showImage &&
        !showPlaceholder && <span className={classes.empty} role="img" aria-label={alt} />
      )}
    </>
  );
}
